import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Paper, Snackbar, TextField, Tooltip, Typography,
  alpha, useTheme,
} from '@mui/material';
import {
  ContentCopy as CopyIcon, OpenInNew as OpenIcon, QrCode2 as QrIcon,
  Public as PublicIcon, LinkOff as RevokeIcon,
} from '@mui/icons-material';
import wineListShareService, { type WineListShare } from '../../services/wineListShareService';
import config from '../../config/environments';

interface Props {
  venueId: string;
  wineListId: string;
  /** WineList.status — drives the DRAFT warning. */
  listStatus: 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
}

/**
 * Publish / share-link manager for a wine list.
 *
 * Sits between the wine-list header card and the tabbed content. Always
 * visible — the publish state should never require a click to discover.
 *
 * - No active share → "Publish Wine List" button
 * - Active share    → URL with copy / open / QR / revoke actions
 * - Warns when list is DRAFT (public route requires status=ACTIVE)
 */
const WineListPublishPanel: React.FC<Props> = ({ venueId, wineListId, listStatus }) => {
  const theme = useTheme();
  const [share, setShare] = useState<WineListShare | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);
  const [copyToast, setCopyToast] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const refresh = () => {
    setLoading(true);
    setError('');
    wineListShareService.getShare(venueId, wineListId)
      .then(setShare)
      .catch(() => setError('Could not load publish status. Please try again.'))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, [venueId, wineListId]);

  // Public customer URL — derived from the same origin as the admin (web bundle
  // is served alongside it). The backend QR endpoint uses its own
  // PUBLIC_WEB_BASE_URL env var; here we use window.location.origin so admins
  // see the URL appropriate to whichever host they're on.
  const publicUrl = share
    ? `${window.location.origin}/m/${encodeURIComponent(share.slug)}`
    : null;

  // QR PNG endpoint on the API. Opens directly in a new tab so the admin can
  // save / print it. apiUrl already ends in /api so we just append the path.
  const qrUrl = share
    ? `${config.apiUrl}/public/wine-list/${encodeURIComponent(share.slug)}/qr?size=512`
    : null;

  const handlePublish = async () => {
    setWorking(true);
    setError('');
    try {
      const created = await wineListShareService.createShare(venueId, wineListId);
      setShare(created);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Could not publish. Please try again.');
    } finally {
      setWorking(false);
    }
  };

  const handleRevoke = async () => {
    setWorking(true);
    setError('');
    try {
      await wineListShareService.revokeShare(venueId, wineListId);
      setShare(null);
      setConfirmRevoke(false);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Could not unpublish. Please try again.');
    } finally {
      setWorking(false);
    }
  };

  const handleCopy = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopyToast(true);
    } catch {
      // Fallback if clipboard API blocked: show in a dialog the user can manually copy.
      window.prompt('Copy the public URL:', publicUrl);
    }
  };

  const isDraft = listStatus === 'DRAFT';

  return (
    <Paper sx={{ p: 2, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1, minWidth: 0 }}>
          <PublicIcon sx={{ color: share ? 'success.main' : 'text.disabled', mt: 0.25 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {share ? 'Public link' : 'Not published'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {share
                ? 'Customers can view this list at the URL below.'
                : 'Publish this list to give it a public URL and QR code customers can open from their phones.'}
            </Typography>
          </Box>
        </Box>

        {!share && !loading && (
          <Button
            variant="contained"
            size="small"
            disabled={working}
            onClick={handlePublish}
            sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
          >
            {working ? <CircularProgress size={16} color="inherit" /> : 'Publish Wine List'}
          </Button>
        )}
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mt: 1.5, borderRadius: 1 }}>
          {error}
        </Alert>
      )}

      {/* DRAFT warning — public route requires status=ACTIVE on the server side. */}
      {share && isDraft && (
        <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 1 }}>
          Set this list to <strong>ACTIVE</strong> before guests can view it. Public requests for a DRAFT list return a "not available" page.
        </Alert>
      )}

      {share && publicUrl && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
            <TextField
              value={publicUrl}
              size="small"
              fullWidth
              slotProps={{
                input: {
                  readOnly: true,
                  sx: {
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                    backgroundColor: alpha(theme.palette.text.primary, 0.03),
                  },
                },
              }}
              onFocus={e => e.target.select()}
            />
            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
              <Tooltip title="Copy link">
                <IconButton size="small" onClick={handleCopy} aria-label="Copy public URL">
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Open preview in new tab">
                <IconButton
                  size="small"
                  component="a"
                  href={publicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open public list"
                >
                  <OpenIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="QR code (opens PNG)">
                <IconButton
                  size="small"
                  component="a"
                  href={qrUrl ?? '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open QR code"
                >
                  <QrIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Unpublish">
                <IconButton
                  size="small"
                  color="error"
                  disabled={working}
                  onClick={() => setConfirmRevoke(true)}
                  aria-label="Unpublish"
                >
                  <RevokeIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      )}

      {/* Revoke confirmation */}
      <Dialog open={confirmRevoke} onClose={() => !working && setConfirmRevoke(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Unpublish wine list?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The public URL will stop working immediately. Anyone who has scanned the QR code or saved the link will see a "not available" page.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            The slug stays reserved — re-publishing will issue a new URL.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmRevoke(false)} disabled={working} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleRevoke}
            disabled={working}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {working ? <CircularProgress size={18} color="inherit" /> : 'Unpublish'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copyToast}
        autoHideDuration={2000}
        onClose={() => setCopyToast(false)}
        message="Link copied"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Paper>
  );
};

export default WineListPublishPanel;
